import React, { FunctionComponent, useEffect, Fragment, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _get from 'lodash/get';

import Icon from '../Icons';
import Summary from './Summary';
import SavedCards from './SavedCards';
import Cards from './Cards';
import UPI from './UPI';
import Netbanking from './Netbanking';
import Accordion from '../Accordion';
import { fetchPaymentOptions, resetPayment } from './action';

import { Container } from './styles';

const Payment: FunctionComponent<any> = (fromFastag) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const amount = useSelector((state: any) => _get(state, 'payment.paymentInfo.data.amount', 0));
  const isLoaded = useSelector((state: any) => _get(state, 'payment.isLoaded', false));
  const paymentInfo = useSelector((state: any) => _get(state, 'payment.paymentInfo.data', {}));
  const paymentOptions = useSelector((state: any) => _get(state, 'payment.paymentOptions', {}));

  const [isSavedCardOpen, toggleSavedCard] = useState(true);
  const [isCardsOpen, toggleCards] = useState(false);
  const [isUpiOpen, toggleUpi] = useState(false);
  const [isNetbankingOpen, toggleNetbanking] = useState(false);
  const [internetCharges, setInternetCharges] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState({});

  const onToggleClick = useCallback(
    (type: string) => {
      const isOpen = returnToggleOpenOption(type);
      isOpen ? updateCharges('', selectedPayment) : updateCharges(type, selectedPayment);
      toggleSavedCard(false);
      toggleCards(false);
      toggleNetbanking(false);
      toggleUpi(false);
      if (type === 'Saved Cards') {
        toggleSavedCard(!isSavedCardOpen);
      }
      if (type === 'Debit / Credit Card') {
        toggleCards(!isCardsOpen);
      }
      if (type === 'UPI') {
        toggleUpi(!isUpiOpen);
      }
      if (type === 'Netbanking') {
        toggleNetbanking(!isNetbankingOpen);
      }
    },
    // TODO: handle dependencies 'returnToggleOpenOption', 'selectedPayment', and 'updateCharges'
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSavedCardOpen, isCardsOpen, isUpiOpen, isNetbankingOpen],
  );

  const returnToggleOpenOption = (type: string) => {
    let flag;
    if (type === 'Saved Cards') {
      flag = isSavedCardOpen;
    } else if (type === 'Debit / Credit Card') {
      flag = isCardsOpen;
    } else if (type === 'UPI') {
      flag = isUpiOpen;
    } else if (type === 'Netbanking') {
      flag = isNetbankingOpen;
    } else {
      flag = false;
    }
    return flag;
  };

  const dispatchToStore = useCallback((payload: any) => dispatch(fetchPaymentOptions(payload)), [dispatch]);

  const getPaymentCharges = (paymentOptions: Array<object>) => {
    if (!(paymentOptions && paymentOptions.length)) {
      return;
    }
    const chargesObject: any = {};
    paymentOptions.forEach((option: any) => {
      let charges = 0;
      option.modeRates.rates.forEach((rate: any) => {
        if (amount > rate.minAmount && amount <= rate.maxAmount) {
          charges = rate.percent;
        }
      });
      chargesObject[option.mode] = charges;
    });
    return chargesObject;
  };

  const updateCharges = (type: string, selectedPaymentObj: any) => {
    const selPayment: any = selectedPaymentObj;
    const paymentCharges: any = getPaymentCharges(paymentInfo.pspModes);
    const rate = selPayment[type] ? paymentCharges[selPayment[type]] : 0;
    setInternetCharges(rate ? (amount * rate) / 100 : 0);
  };

  const updateInternetCharges = (paymentObj: any) => {
    const selectedPaymentObj = { ...selectedPayment, ...paymentObj };
    setSelectedPayment((prev) => ({ ...prev, ...paymentObj }));
    const paymentType = Object.keys(paymentObj)[0];
    updateCharges(paymentType, selectedPaymentObj);
  };

  const handleBack = () => {
    const referralUrl = localStorage.getItem('referralUrl');
    if (referralUrl) {
      history.push(referralUrl);
    } else {
      history.goBack();
    }
  };

  useEffect(() => {
    if (!isLoaded && history.action === 'PUSH') {
      return;
    }
    if (!amount) {
      history.goBack();
      return;
    }
    dispatchToStore({
      mId: paymentInfo.merchantId,
      orderId: paymentInfo.transactionCode,
      token: paymentInfo.checkSumHash,
    });
    return () => {
      dispatch(resetPayment());
      if (history.action === 'POP') {
        handleBack();
      }
    };
    // TODO: handle dependencies 'amount', 'dispatch', 'handleBack', 'history', 'paymentInfo'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, dispatchToStore]);

  const savedCards = _get(paymentOptions, 'merchantPayOption.savedInstruments', []);
  const paymentModes = _get(paymentOptions, 'merchantPayOption.paymentModes', []);
  const paymentCharges = getPaymentCharges(paymentInfo.pspModes);

  return (
    <Fragment>
      {amount ? (
        <Container className="wfull">
          <p className="warning-msg bg-light-tan font-red largest p16 text-center mb16">
            Do not refresh or press ‘Back’ button
          </p>
          <div className="mode-container">
            <div className="mt5 bg-white p16">
              <div className="cursorP flex falign-c" onClick={handleBack}>
                <span className="back-icon">
                  <Icon name="arrow" />
                </span>{' '}
                Back
              </div>
            </div>
            <Summary amount={amount} internetCharges={internetCharges} />
            {savedCards && savedCards.length ? (
              <div className="mb16 bg-white">
                <Accordion
                  title="Saved Cards"
                  key="Saved Cards"
                  clickHandler={onToggleClick}
                  open={returnToggleOpenOption('Saved Cards')}
                  icon="payment"
                  referenceClass="falign-c p16 pb8"
                  subtitle={`Internet Charges: Debit Card (${paymentCharges['DC']}%) / Credit Card (${paymentCharges['CC']}%)`}
                >
                  <SavedCards
                    list={savedCards}
                    amount={amount}
                    internetCharges={internetCharges}
                    type="Saved Cards"
                    updateInternetCharges={updateInternetCharges}
                    paymentInfo={paymentInfo}
                    fromFastag={fromFastag}
                  />
                </Accordion>
              </div>
            ) : null}
            <div className="mb16 bg-white">
              <Accordion
                title="Debit / Credit Card"
                key="Cards"
                clickHandler={onToggleClick}
                open={returnToggleOpenOption('Debit / Credit Card')}
                icon="payment"
                referenceClass="falign-c p16 pb8"
                subtitle={`Internet Charges: Debit Card (${paymentCharges['DC']}%) / Credit Card (${paymentCharges['CC']}%)`}
              >
                <Cards
                  paymentInfo={paymentInfo}
                  type="Debit / Credit Card"
                  updateInternetCharges={updateInternetCharges}
                  amount={amount}
                  internetCharges={internetCharges}
                  fromFastag={fromFastag}
                />
              </Accordion>
            </div>
            <div className="mb16 bg-white">
              <Accordion
                title="UPI"
                key="UPI"
                clickHandler={onToggleClick}
                open={returnToggleOpenOption('UPI')}
                referenceClass="falign-c p16 pb8"
                subtitle={`Internet Charges: ${paymentCharges['UPI']}%`}
              >
                <UPI
                  paymentInfo={paymentInfo}
                  amount={amount}
                  type="UPI"
                  updateInternetCharges={updateInternetCharges}
                  internetCharges={internetCharges}
                  fromFastag={fromFastag}
                />
              </Accordion>
            </div>
            {paymentModes && paymentModes.length ? (
              <div className="mb16 bg-white">
                <Accordion
                  title="Netbanking"
                  key="Netbanking"
                  clickHandler={onToggleClick}
                  open={returnToggleOpenOption('Netbanking')}
                  referenceClass="falign-c p16 pb8"
                  subtitle={`Internet Charges: ${paymentCharges['NB']}%`}
                >
                  <Netbanking
                    paymentModes={paymentModes}
                    amount={amount}
                    type="Netbanking"
                    updateInternetCharges={updateInternetCharges}
                    paymentInfo={paymentInfo}
                    internetCharges={internetCharges}
                    fromFastag={fromFastag}
                  />
                </Accordion>
              </div>
            ) : null}
            <p className="small p8 pl16 pr16 font-success bg-success mt16">100% Safe Payments</p>
          </div>
        </Container>
      ) : null}
    </Fragment>
  );
};

export default Payment;
